import React from 'react';
import I18n from 'i18n-js';
import {
  core_apimessages_Money as Money,
  core_apimessages_Error as Error,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
} from '../../src/schemas/rql';
import { useUser, useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import { inMobileBrowser } from '@reverbdotcom/commons/src/user_context_helpers';
import CondensedOrderSummary from './condensed_order_summary';
import { useApplePayAvailabilityByConfig } from './useApplePayAvailability';
import experiments from '@reverbdotcom/commons/src/experiments';
import { CheckoutErrorAlertBox } from './checkout/CheckoutErrorAlertBox';
import CartApplePayButton from './CartApplePayButton';
import CheckoutPayPalButton, { isCartEligible } from './CheckoutPayPalButton';
import classNames from 'classnames';
import URLHelpers from './shared/url_helpers';
import { findMerchantId, merchantName } from './checkoutApplePay';
import { ApplePayConfig } from './useCartApplePay';
import { PAYPAL_PRESENTMENT_CURRENCIES } from '@reverbdotcom/commons/src/currency';

interface Props {
  paymentMethodType: string;
  checkoutBundlingId: string;
  applePayCheckoutBundlingId: string;
  amountTotal: Money
  adyenCheckoutPaymentMethodsConfig: string;
  currency: string;
  paypalCheckoutExpAttributes: {
    currencyCount: number,
    bundleCount: number,
    shopCount: number,
    isLocalPickupOnly: boolean,
    includesStandalonePaypal: boolean,
  },
  showReverbProtectionCallout: boolean,
}

export default function CartHoistedExpressPayWrapper(props: Props) {
  const user = useUser();
  const [errors, setErrors] = React.useState([]);

  const [isApplePayAvailable] = useApplePayAvailabilityByConfig(props.adyenCheckoutPaymentMethodsConfig);
  const showApplePayButton = isApplePayAvailable && props.paymentMethodType == CheckoutPaymentMethodType.DIRECT_CHECKOUT;

  const isCartEligibleForPaypalExp = isCartEligible(props.paypalCheckoutExpAttributes);
  const inPaypalButtonRollout = useExpEnabled(experiments.PAYPAL_CHECKOUT_BUTTON);
  const showPaypalButton = inPaypalButtonRollout && isCartEligibleForPaypalExp;

  const showExpressPay = inPaypalButtonRollout
    && (showApplePayButton || showPaypalButton);

  const classes = classNames(
    'checkout-cart-header',
    { 'checkout-cart-header--express': inPaypalButtonRollout },
  );

  if (!showExpressPay && !showApplePayButton) {
    return null;
  }

  const applePayConfig: ApplePayConfig = {
    merchantId: findMerchantId(JSON.parse(props.adyenCheckoutPaymentMethodsConfig)),
    merchantName: merchantName(),
  };

  return (
    <div className={classes}>
      {inMobileBrowser(user) &&
        <CondensedOrderSummary
          itemPlusShippingSubtotal={props.amountTotal.display}
        />
      }
      {showExpressPay && props.showReverbProtectionCallout &&
        <div className="mtb-3">
          <p className="cart-payment-methods__protection">
            {I18n.t('discovery.paymentMethodSelection.protection.buyWithConfidence')}
            <span className="ml-space">
              <a
                href={URLHelpers.reverbProtectionPath}
                target="_blank"
                rel="noreferrer"
                className="text-link"
              >
                {I18n.t('discovery.paymentMethodSelection.protection.protectionIncluded')}
              </a>
            </span>
          </p>
        </div>
      }
      {showExpressPay &&
        <>
          {errors.length > 0 &&
            <div className="mb-2">
              <CheckoutErrorAlertBox errors={errors} />
            </div>
          }
          <CartExpressPayWrapper
            amountTotal={props.amountTotal}
            applePayConfig={applePayConfig}
            showPaypalCheckoutButton={showPaypalButton}
            applePayCheckoutBundlingId={props.applePayCheckoutBundlingId}
            checkoutBundlingId={props.checkoutBundlingId}
            onError={setErrors}
            currency={props.currency}
            showApplePayButton={showApplePayButton}
          />
        </>
      }
      {inMobileBrowser(user) && !showExpressPay && showApplePayButton &&
        <div className="checkout-cart-header__button">
          <div className="checkout-cart-header__button--apple-pay">
            <CartApplePayButton
              variant="black"
              checkoutBundlingId={props.checkoutBundlingId}
              amountTotal={props.amountTotal}
              applePayConfig={applePayConfig}
              tracking={{
                displayStyle: 'hoisted',
              }}
            />
          </div>
        </div>
      }
    </div>
  );
}

interface ExpressPayWrapperProps {
  applePayCheckoutBundlingId: string;
  applePayConfig: ApplePayConfig;
  showPaypalCheckoutButton: boolean;
  onError: (errors: Error[]) => void;
  checkoutBundlingId: string;
  amountTotal: Money;
  currency: string;
  showApplePayButton: boolean;
}

export function CartExpressPayWrapper(props: ExpressPayWrapperProps) {
  const user = useUser();
  const inPayPalPresentmentCurrencyRollout = useExpEnabled(experiments.DC_PAYPAL_ALL_THE_PRESENTMENT_CURRENCIES);
  const supportedCurrency = PAYPAL_PRESENTMENT_CURRENCIES.includes(user.currency);
  const paypalCurrency = inPayPalPresentmentCurrencyRollout && supportedCurrency ? user.currency : props.currency;

  if (inMobileBrowser(user)) {
    return mobileVariant(paypalCurrency);
  } else {
    return desktopVariant(paypalCurrency);
  }

  function mobileVariant(paypalCurrency) {
    return (
      <>
        <h2 className="weight-bold size-140 mb-2">{I18n.t('discovery.checkout.expressCheckout.title')}</h2>
        <div className="mb-1">
          {props.showPaypalCheckoutButton &&
            <CheckoutPayPalButton
              currency={paypalCurrency}
              checkoutBundlingId={props.checkoutBundlingId}
              onError={props.onError}
              variant="cart"
              parentComponentName="CartHoistedExpressPayWrapper"
            />
          }
        </div>
        {props.showApplePayButton &&
          <CartApplePayButton
            variant="black-express"
            checkoutBundlingId={props.applePayCheckoutBundlingId}
            amountTotal={props.amountTotal}
            applePayConfig={props.applePayConfig}
            tracking={{
              displayStyle: 'hoisted',
            }}
          />
        }
        <div className="express-pay-separator">{I18n.t('discovery.checkout.expressCheckout.separatorText')}</div>
      </>
    );
  }

  function desktopVariant(paypalCurrency) {
    return (
      <>
        <div className="d-flex fx-dir-col fx-align-center">
          <h2 className="weight-bold size-140 mb-2">{I18n.t('discovery.checkout.expressCheckout.title')}</h2>
          {props.showPaypalCheckoutButton &&
            <div className="mb-1 width-rem-50">
              <CheckoutPayPalButton
                currency={paypalCurrency}
                checkoutBundlingId={props.checkoutBundlingId}
                onError={props.onError}
                variant="cart"
                parentComponentName="CartHoistedExpressPayWrapper"
              />
            </div>
          }
          {props.showApplePayButton &&
            <div className="width-rem-50">
              <CartApplePayButton
                variant="black-express"
                checkoutBundlingId={props.applePayCheckoutBundlingId}
                amountTotal={props.amountTotal}
                applePayConfig={props.applePayConfig}
                tracking={{
                  displayStyle: 'hoisted',
                }}
              />
            </div>
          }
        </div>
        <div className="express-pay-separator--shortened">{I18n.t('discovery.checkout.expressCheckout.separatorText')}</div>
      </>
    );
  }
}
